import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "./AuthContext";
import demoVideo from "./assets/introok.mp4";
import HeaderRunwayStyle from "./HeaderRunwayStyle";
import FooterRunwayStyle from "./FooterRunwayStyle";
import CustomLoginForm from "./CustomLoginForm";
import "./LandingPage.css";


const LP_LANG_KEY = "weavevo_lang";

/**
 * Minimal landing-page i18n.
 * Default language: English ("en")
 * Optional: French ("fr")
 */
const LP_I18N = {
  en: {
    // Common
    open_aria: "Open {title}",
    generic_video_title: "Video",

    // Process / pipeline
    how_aria: "Creation process",
    process_preview_aria: "Process preview",
    step_script: "Script",
    step_invite: "Invite",
    step_collect: "Collect",
    step_edit: "Edit",
    step_export: "Export",
    pipe_mode_collecte: "Collect",
    pipe_mode_orchestration: "Orchestrate",
    pipe_mode_diffusion: "Share",

    // Use case showcase
    usecase_aria: "Use case showcase",
    usecase_kicker: "Real use cases",
    usecase_title: "Some real use cases",
    usecase_sub:
      "Two real stories that show Weavevo in action — from multi‑Weaver collection to a single final video (voice‑over, music, transitions), with everyone credited.",
    usecase_left_title: "Contributions",
    usecase_left_sub: "A few clips uploaded by the team.",
    usecase_right_title: "Final video",
    usecase_right_sub: "Auto-assembled with voice-over, music and transitions.",
    usecase_project_pill: "Weekend recap",
    usecase_stats: "{count} contributors • {clips} clips",
    usecase_credit: "All contributors included",
    usecase_watch: "Watch the final weave",

    usecase_templates_aria: "Use case templates",
    usecase_tpl1_title: "Zookeeper control & care",
    usecase_tpl1_sub:
      "Keepers upload short clips (feeding, training, health checks) — Weavevo builds a daily monitoring recap.",
    usecase_tpl1_b1: "Who: keepers on mobile",
    usecase_tpl1_b2: "Collect: 5–15s updates + tags",
    usecase_tpl1_b3: "Output: a 1–2 min daily recap",
    usecase_tpl2_title: "A story told by the world",
    usecase_tpl2_sub:
      "People worldwide film the same prompt — Weavevo weaves it into one narrative video.",
    usecase_tpl2_b1: "Who: a global community",
    usecase_tpl2_b2: "Collect: scenes, voice notes, context",
    usecase_tpl2_b3: "Output: one shared story + credits",
    usecase_board_zoo_chip: "Practitioners",
    usecase_board_world_chip: "Global contributors",
    usecase_collect_label: "Collect clips",
    usecase_final_label: "Final weave",
    usecase_footer_vo: "Voice-over",
    usecase_footer_music: "Music",
    usecase_footer_transitions: "Transitions",
    usecase_zoo_hint: "Feeding • Training • Health checks",
    usecase_world_hint: "Same prompt • Many perspectives",
    usecase_zoo_clip1: "Feeding",
    usecase_zoo_clip2: "Health check",
    usecase_zoo_clip3: "Training",
    usecase_zoo_tile1: "Daily log",
    usecase_zoo_tile2: "Voice",
    usecase_zoo_tile3: "Music",
    usecase_zoo_tile4: "Notes",
    usecase_zoo_tile5: "Transitions",
    usecase_world_clip1: "Cities",
    usecase_world_clip2: "Skies",
    usecase_world_clip3: "Moments",
    usecase_world_tile1: "One story",
    usecase_world_tile2: "Global VO",
    usecase_world_tile3: "Captions",
    usecase_world_tile4: "Credits",
    usecase_world_tile5: "Flow",



    // Pipeline
    pipeline_kicker: "Collaboration",
    pipeline_title: "From collaboration to video — in a few steps",
    pipeline_sub: "Collect media, orchestrate the story, then export a clean video.",
    pipeline_modes_aria: "Pipeline modes",
    pipeline_visual_title: "Workflow",
    pipeline_visual_badge: "Fast",
    pipeline_visual_aria: "Workflow preview",
    pipeline_output_title: "Output",
    pipeline_output_badge: "Auto",
    pipeline_contrib_aria: "Contributors",
    pipeline_strip_aria: "Sample clips",

    // Hero
    hero_kicker: "Weavevo — the platform for Weavers",
    hero_title_line1: "Don't just create.",
    hero_title_line2: "Weave the world.",
    hero_subheadline:
      "Move beyond broadcasting. Weavevo unites Creators and Weavers to bring grand visions to life through shared perspectives.",
    hero_sub_before_maestro: "The ",
    hero_sub_between_maestro_weavers: " orchestrates the project, the ",
    hero_sub_between_weavers_weave:
      " upload their media — Weavevo assembles and exports a clean ",
    hero_sub_after_weave: " in the right format.",
    hero_bullet_1: "Creator: script + validation in a guided flow",
    hero_bullet_2: "Weavers: fast upload, tags, discussion — smooth gallery (masonry + lazy)",
    hero_bullet_3: "Weave: voice-over, music, transitions, exports 16:9 / 1:1 / 9:16",
    btn_discover: "Join as a Weaver",
    btn_workflow: "See the workflow",
    btn_start: "Start Your First Weave",
    login_title: "Weaver access",
    login_sub: "Welcome To Weavevo",
    login_note: "Welcome to Weavevo",

    // Carousel ("Get your next")
    gyc_prefix: "Get your next",
    gyc_prev: "Previous",
    gyc_next: "Next",
    gyc_dots_aria: "Carousel navigation",
    gyc_goto: "Go to slide {n}",
    gyc_pill_ai_curated: "AI-curated",
    gyc_pill_creator_ready: "Creator-ready",
    gyc_pill_global: "Global",
    gyc_pill_fast: "Fast",


    // For Creators
    creators_aria: "For Creators value proposition",
    creators_kicker: "Creators",
    creators_title: "Your Vision, Woven by the World",
    creators_sub:
      "Scale your content with global contributions. Let AI curate the best threads from your Weavers to build your next masterpiece.",
    creators_card1_title: "Script & structure",
    creators_card1_text: "Write steps, locations and prompts so everyone stays aligned.",
    creators_card2_title: "Invite & coordinate",
    creators_card2_text: "Select Weavers, set visibility, and track progress in one place.",
    creators_card3_title: "Review & approve",
    creators_card3_text: "See drafts, request edits, then validate the final Weave.",
    creators_card4_title: "Export & share",
    creators_card4_text: "16:9, 1:1, 9:16 — ready for YouTube, Instagram and TikTok.",

    // Creators carousel slides
    creators_slide1_title: "Global Perspectives",
    creators_slide1_text: "Weave your story with clips and frames captured by Weavers across the globe.",
    creators_slide2_title: "Massive Production",
    creators_slide2_text: "Turn your followers into a professional-grade film crew.",
    creators_slide3_title: "Smart Selection",
    creators_slide3_text: "Let AI filter thousands of contributions to find the \"golden threads\".",
    creators_slide4_title: "The Power of Truth",
    creators_slide4_text: "Turn your documentary into a living archive where every Weaver adds a unique layer of truth, research, or art.",
    creators_slide5_title: "Pure Inspiration",
    creators_slide5_text: "Browse your infinite library of shared media to spark your next creative breakthrough.",

    // For Weavers
    weavers_aria: "For Weavers community",
    weavers_kicker: "Weavers",
    weavers_title: "Be the Thread that Connects the World",
    weavers_sub:
      "Don’t just record, contribute to global narratives. Share your unique perspective and see your shots woven into professional documentaries, news, and viral masterpieces by creators worldwide.",
    weavers_card1_title: "Fast upload",
    weavers_card1_text: "Drag & drop videos or photos. No friction.",
    weavers_card2_title: "Tags that matter",
    weavers_card2_text: "Add context so the montage makes sense.",
    weavers_card3_title: "Chat & reactions",
    weavers_card3_text: "Coordinate the story with the team in real time.",
    weavers_card4_title: "Your credit, your profile",
    weavers_card4_text: "Your name and avatar appear in the recap and final video.",

    // Weavers carousel slides
    weavers_slide1_title: "Global Impact",
    weavers_slide1_text: "Turn your daily captures into essential pieces of a global story, reaching audiences you never imagined.",
    weavers_slide2_title: "Artistic Legacy",
    weavers_slide2_text: "Turn your raw footage into a lasting contribution within a living archive of truth and creativity.",
    weavers_slide3_title: "Instant Collaboration",
    weavers_slide3_text: "Join a \"Weave\" in seconds, whether it’s an open public project or an exclusive private collaboration.",
    weavers_slide4_title: "AI-Enhanced Visibility",
    weavers_slide4_text: "Let AI showcase your best work to top creators by identifying your \"golden threads\" automatically.",
    weavers_slide5_title: "Limitless Inspiration",
    weavers_slide5_text: "Browse the library to see how others capture the world — and spark your next contribution.",

    usecase_examples_title: "Examples",
    usecase_ex1_title: "Team event recap",
    usecase_ex1_text: "Collect short clips from everyone — publish one clean recap.",
    usecase_ex2_title: "Product story",
    usecase_ex2_text: "From demo shots to testimonials, assembled in one narrative.",
    usecase_ex3_title: "Travel memories",
    usecase_ex3_text: "Multiple phones, one story — music + voice-over included.",
    usecase_ex4_title: "Training & knowledge",
    usecase_ex4_text: "Turn scattered recordings into a structured learning video.",

    // CTA
    cta_kicker: "Start a project",
    cta_title: "Ready to orchestrate a collaborative video?",
    cta_text:
      "Create a project, invite contributors, then generate a video from the collected media and your script.",
    cta_btn_create: "Create my account",
    cta_btn_explore: "Explore the gallery",
  },

  fr: {
    // Common
    open_aria: "Ouvrir {title}",
    generic_video_title: "Vidéo",

    // Process / pipeline
    how_aria: "Processus de création",
    process_preview_aria: "Aperçu du processus",
    step_script: "Scénario",
    step_invite: "Inviter",
    step_collect: "Collecter",
    step_edit: "Monter",
    step_export: "Exporter",
    pipe_mode_collecte: "Collecte",
    pipe_mode_orchestration: "Orchestration",
    pipe_mode_diffusion: "Diffusion",

    // Use case showcase
    usecase_aria: "Cas d’usage",
    usecase_kicker: "Cas d’usage",
    usecase_title: "Quelques cas d’usage concrets",
    usecase_sub:
      "Deux histoires réelles pour voir Weavevo en action — collecte multi‑Weavers, puis une seule vidéo finale (voix off, musique, transitions), avec tous les crédits.",
    usecase_left_title: "Contributions",
    usecase_left_sub: "Quelques clips déposés par l’équipe.",
    usecase_right_title: "Vidéo finale",
    usecase_right_sub: "Montage auto avec voix-off, musique et transitions.",
    usecase_project_pill: "Récap week-end",
    usecase_stats: "{count} contributeurs • {clips} clips",
    usecase_credit: "Tous les contributeurs inclus",
    usecase_watch: "Voir le Weave final",

    usecase_templates_aria: "Modèles de cas d’usage",
    usecase_tpl1_title: "Contrôle & soins des animaux",
    usecase_tpl1_sub:
      "Les soigneurs uploadent de courts clips (nourrissage, entraînement, contrôle santé) — Weavevo construit un récap de suivi quotidien.",
    usecase_tpl1_b1: "Qui : soigneurs sur mobile",
    usecase_tpl1_b2: "Collecte : updates 5–15s + tags",
    usecase_tpl1_b3: "Sortie : récap quotidien 1–2 min",
    usecase_tpl2_title: "Une histoire racontée par le monde",
    usecase_tpl2_sub:
      "Partout dans le monde, on filme le même prompt — Weavevo tisse une seule vidéo narrative.",
    usecase_tpl2_b1: "Qui : une communauté mondiale",
    usecase_tpl2_b2: "Collecte : scènes, notes vocales, contexte",
    usecase_tpl2_b3: "Sortie : une histoire partagée + crédits",
    usecase_board_zoo_chip: "Soigneurs",
    usecase_board_world_chip: "Contributeurs mondiaux",
    usecase_collect_label: "Collecte des clips",
    usecase_final_label: "Vidéo finale",
    usecase_footer_vo: "Voix off",
    usecase_footer_music: "Musique",
    usecase_footer_transitions: "Transitions",
    usecase_zoo_hint: "Repas • Entraînement • Contrôles",
    usecase_world_hint: "Même prompt • Regards multiples",
    usecase_zoo_clip1: "Repas",
    usecase_zoo_clip2: "Contrôle santé",
    usecase_zoo_clip3: "Entraînement",
    usecase_zoo_tile1: "Journal",
    usecase_zoo_tile2: "Voix",
    usecase_zoo_tile3: "Musique",
    usecase_zoo_tile4: "Notes",
    usecase_zoo_tile5: "Transitions",
    usecase_world_clip1: "Villes",
    usecase_world_clip2: "Ciels",
    usecase_world_clip3: "Moments",
    usecase_world_tile1: "Une histoire",
    usecase_world_tile2: "Voix globale",
    usecase_world_tile3: "Sous-titres",
    usecase_world_tile4: "Crédits",
    usecase_world_tile5: "Flux",



    // Pipeline
    pipeline_kicker: "Collaboration",
    pipeline_title: "De la collaboration à la vidéo — en quelques étapes",
    pipeline_sub: "Collectez des médias, orchestrez l’histoire, puis exportez une vidéo propre.",
    pipeline_modes_aria: "Modes du workflow",
    pipeline_visual_title: "Parcours",
    pipeline_visual_badge: "Rapide",
    pipeline_visual_aria: "Aperçu du parcours",
    pipeline_output_title: "Sortie",
    pipeline_output_badge: "Auto",
    pipeline_contrib_aria: "Contributeurs",
    pipeline_strip_aria: "Extraits",

    // Hero
    hero_kicker: "Weavevo — la plateforme des Weavers",
    hero_title_line1: "Ne vous contentez pas de créer.",
    hero_title_line2: "Tissez le monde.",
    hero_subheadline:
      "Allez au-delà de la diffusion. Weavevo réunit les Creators et les Weavers pour donner vie à de grandes visions grâce à des perspectives partagées.",
    hero_sub_before_maestro: "Le ",
    hero_sub_between_maestro_weavers: " orchestre le projet, les ",
    hero_sub_between_weavers_weave: " déposent leurs médias, Weavevo assemble et exporte en un ",
    hero_sub_after_weave: " propre — au bon format.",
    hero_bullet_1: "Creator : scénario + validation dans un flux guidé",
    hero_bullet_2: "Weavers : upload rapide, tags, discussion — galerie fluide (masonry + lazy)",
    hero_bullet_3: "Weave : voix-off, musique, transitions et exports 16:9 / 1:1 / 9:16",
    btn_discover: "Rejoindre en tant que Weaver",
    btn_workflow: "Voir le workflow",
    btn_start: "Démarrer mon premier Weave",
    login_title: "Accès Weaver",
    login_sub: "Bienvenue sur Weavevo",
    login_note: "Bienvenue sur Weavevo",

    // Carrousel ("Trouvez votre prochaine…")
    gyc_prefix: "Trouvez votre prochaine",
    gyc_prev: "Précédent",
    gyc_next: "Suivant",
    gyc_dots_aria: "Navigation du carrousel",
    gyc_goto: "Aller à la diapositive {n}",
    gyc_pill_ai_curated: "Sélection IA",
    gyc_pill_creator_ready: "Prêt pour creators",
    gyc_pill_global: "Global",
    gyc_pill_fast: "Rapide",


    // Pour les Creators
    creators_aria: "Proposition de valeur pour les Creators",
    creators_kicker: "Creators",
    creators_title: "Votre vision, tissée par le monde",
    creators_sub:
      "Faites grandir votre contenu grâce à des contributions du monde entier. Laissez l’IA sélectionner les meilleurs fils de vos Weavers pour créer votre prochain chef‑d’œuvre.",
    creators_card1_title: "Scénario & structure",
    creators_card1_text: "Rédigez des étapes, lieux et consignes pour garder tout le monde aligné.",
    creators_card2_title: "Inviter & coordonner",
    creators_card2_text: "Sélectionnez les Weavers, réglez la visibilité, suivez l’avancement au même endroit.",
    creators_card3_title: "Revoir & valider",
    creators_card3_text: "Consultez les brouillons, demandez des ajustements, puis validez le Weave final.",
    creators_card4_title: "Exporter & diffuser",
    creators_card4_text: "16:9, 1:1, 9:16 — prêt pour YouTube, Instagram et TikTok.",

    // Slides carrousel Creators
    creators_slide1_title: "Perspectives globales",
    creators_slide1_text: "Tissez votre histoire avec des clips et des images capturés par des Weavers partout dans le monde.",
    creators_slide2_title: "Production massive",
    creators_slide2_text: "Transformez vos abonnés en équipe de tournage de niveau professionnel.",
    creators_slide3_title: "Sélection intelligente",
    creators_slide3_text: "Laissez l’IA filtrer des milliers de contributions pour trouver les \"fils d’or\".",
    creators_slide4_title: "La force du vrai",
    creators_slide4_text: "Faites de votre documentaire une archive vivante où chaque Weaver ajoute une couche unique de vérité, de recherche ou d’art.",
    creators_slide5_title: "Inspiration pure",
    creators_slide5_text: "Parcourez votre bibliothèque infinie de médias partagés pour déclencher votre prochain déclic créatif.",

    // Pour les Weavers
    weavers_aria: "Communauté Weavers",
    weavers_kicker: "Weavers",
    weavers_title: "Soyez le fil qui relie le monde",
    weavers_sub:
      "Ne faites pas que filmer : contribuez à des récits mondiaux. Partagez votre perspective et voyez vos plans tissés dans des documentaires, actualités ou vidéos virales créés par des creators du monde entier.",
    weavers_card1_title: "Upload rapide",
    weavers_card1_text: "Glissez-déposez vidéos ou photos. Zéro friction.",
    weavers_card2_title: "Des tags utiles",
    weavers_card2_text: "Ajoutez du contexte pour un montage cohérent.",
    weavers_card3_title: "Chat & réactions",
    weavers_card3_text: "Coordonnez l’histoire avec l’équipe en temps réel.",
    weavers_card4_title: "Crédit & profil",
    weavers_card4_text: "Votre nom et votre avatar apparaissent dans le récap et la vidéo finale.",

    // Slides carrousel Weavers
    weavers_slide1_title: "Impact global",
    weavers_slide1_text: "Transformez vos captures du quotidien en pièces essentielles d’un récit mondial, vues par des audiences que vous n’imaginiez pas.",
    weavers_slide2_title: "Héritage artistique",
    weavers_slide2_text: "Faites de vos rushs une contribution durable au sein d’une archive vivante de vérité et de créativité.",
    weavers_slide3_title: "Collaboration instantanée",
    weavers_slide3_text: "Rejoignez un \"Weave\" en quelques secondes, qu’il soit public et ouvert ou privé et exclusif.",
    weavers_slide4_title: "Visibilité boostée par l’IA",
    weavers_slide4_text: "Laissez l’IA mettre en avant votre meilleur contenu auprès des creators en identifiant automatiquement vos \"fils d’or\".",
    weavers_slide5_title: "Inspiration sans limite",
    weavers_slide5_text: "Parcourez la bibliothèque pour voir comment les autres capturent le monde — et trouvez des idées pour votre prochaine contribution.",

    usecase_examples_title: "Exemples",
    usecase_ex1_title: "Récap d’événement d’équipe",
    usecase_ex1_text: "Collectez des clips de chacun — publiez un récap propre et unique.",
    usecase_ex2_title: "Storytelling produit",
    usecase_ex2_text: "Démos + témoignages, assemblés dans une narration claire.",
    usecase_ex3_title: "Souvenirs de voyage",
    usecase_ex3_text: "Plusieurs téléphones, une histoire — musique + voix off incluses.",
    usecase_ex4_title: "Formation & partage",
    usecase_ex4_text: "Transformez des enregistrements dispersés en vidéo pédagogique.",

    // CTA
    cta_kicker: "Lancer un projet",
    cta_title: "Prêt à orchestrer une vidéo collaborative ?",
    cta_text:
      "Créez un projet, invitez des contributeurs, puis générez une vidéo à partir des médias collectés et du scénario.",
    cta_btn_create: "Créer mon compte",
    cta_btn_explore: "Explorer la galerie",
  },
};



const LP_STATIC_CREATORS = [
  { id: "u1", name: "Aïcha" },
  { id: "u2", name: "Omar" },
  { id: "u3", name: "Lina" },
  { id: "u4", name: "Yassine" },
  { id: "u5", name: "Maya" },
  { id: "u6", name: "Sami" },
  { id: "u7", name: "Nour" },
  { id: "u8", name: "Hugo" },
  { id: "u9", name: "Sarah" },
  { id: "u10", name: "Mehdi" },
];
const LP_STATIC_UPLOAD_VIDEOS = [
  "/landing/852352-hd_1920_1080_30fps.mp4",
  "/landing/1987421-hd_1920_1080_30fps.mp4",
  "/landing/2005977-hd_1920_1080_24fps.mp4",
  "/landing/2075385-uhd_3840_1634_25fps.mp4",
  "/landing/2146397-uhd_3840_2160_24fps.mp4",
  "/landing/3018500-hd_1920_1080_24fps.mp4",
  "/landing/3018669-hd_1920_1080_24fps.mp4",
  "/landing/3573956-uhd_3840_2160_30fps.mp4",
  "/landing/4510624-uhd_3840_2160_24fps.mp4",
  "/landing/4125042-uhd_3840_2160_24fps.mp4",
  "/landing/5750805-hd_1920_1080_24fps.mp4",
  "/landing/15040939_2160_3840_60fps.mp4",
  "/landing/15053015_1920_1080_25fps.mp4",
  "/landing/1752834439329-media.mp4",
  "/landing/1760182923599-media.mp4",
  "/landing/1762034022655-media.mp4",
  "/landing/1763663833281-media.mp4",
  "/landing/1765462229510-119-trim.mp4",
  "/landing/1765532104282-media.mp4",
  "/landing/4174112-hd_1920_1080_30fps.mp4",
  "/landing/6622451-hd_1920_1080_30fps.mp4",
  "/landing/2881967-uhd_3840_2160_24fps.mp4",
  "/landing/2881967-uhd_3840_2160_24fps.mp4",
  "/landing/1721316-hd_1920_1080_25fps.mp4",
  "/landing/3015532-hd_1920_1080_24fps.mp4",
  "/landing/1987421-hd_1920_1080_30fps.mp4",
  "/landing/1990658-hd_1280_720_23fps.mp4",
  "/landing/2104648-hd_1280_720_30fps.mp4",

];

const LP_STATIC_MEDIA = LP_STATIC_UPLOAD_VIDEOS.map((file, idx) => {
  const c = LP_STATIC_CREATORS[idx % LP_STATIC_CREATORS.length];
  return {
    id: idx + 1,
    title: `Extrait #${idx + 1}`,
    file_path: file, // ✅ served from /landing/<file> (frontend build)
    visibility: "PUBLIC",
    type: "video",
    mime_type: "video/mp4",
    // ...
  };
});


// Memorize measured aspect ratios to keep masonry stable
const LP_MEDIA_AR_CACHE = new Map(); // src -> aspectRatio (width/height)

/**
 * Lazy media loader (Pinterest-like tile)
 * - Loads only near viewport (with safe fallback if IntersectionObserver is unavailable)
 * - Shows FULL media (no crop) with object-fit: contain
 * - For portrait videos: halve the tile height (still full video visible)
 */
function LazyMediaVisual({
  isImage,
  src,
  title,
  isVideo,
  className = "",
  videoPreload = "metadata",
  loop = false,
}) {
  const holderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  const [aspectRatio, setAspectRatio] = useState(() => {
    const cached = LP_MEDIA_AR_CACHE.get(src);
    return typeof cached === "number" && Number.isFinite(cached) && cached > 0 ? cached : 4 / 3;
  });

  const setARFromWH = useCallback(
    (w, h) => {
      const W = Number(w);
      const H = Number(h);
      if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) return;

      const r = W / H;
      if (!Number.isFinite(r) || r <= 0.05 || r >= 50) return;

      LP_MEDIA_AR_CACHE.set(src, r);
      setAspectRatio(r);
    },
    [src]
  );

  const displayAspectRatio = useMemo(() => {
    const r = aspectRatio;
    if (!Number.isFinite(r) || r <= 0) return r;
    // portrait video: make the tile shorter while still showing full media
    return isVideo && r < 1 ? r * 2 : r;
  }, [aspectRatio, isVideo]);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    // ✅ Mobile/embedded fallback: if IntersectionObserver isn't available, load immediately.
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "700px 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);


  const mediaClass = `${isVideo ? "lp-pin-thumb lp-hoverVideo" : "lp-pin-thumb"} ${className}`.trim();

  return (
    <div
      ref={holderRef}
      className="lp-pin-thumbWrap"
      style={{ aspectRatio: displayAspectRatio, borderRadius: 18, overflow: "hidden", background: "#111" }}
    >
      {!shouldLoad ? (
        <div className="lp-pin-skeleton" style={{ width: "100%", height: "100%" }} />
      ) : isImage ? (
        <img
          src={src}
          alt={title || "media"}
          className={mediaClass}
          loading="lazy"
          decoding="async"
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          onLoad={(e) => {
            const img = e.currentTarget;
            setARFromWH(img?.naturalWidth, img?.naturalHeight);
          }}
        />
      ) : (
        <video
          src={src}
          preload={videoPreload || "metadata"}
          className={mediaClass}
          muted
          playsInline
          loop={loop}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: "#000" }}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            setARFromWH(v?.videoWidth, v?.videoHeight);

            // Try to show an early frame (safe on most browsers; ignored if blocked)
            try {
              if (typeof v?.currentTime === "number") v.currentTime = Math.min(0.1, v.duration || 0.1);
            } catch {
              // ignore
            }
          }}
          onLoadedData={(e) => {
            const v = e.currentTarget;
            // Force first frame render on mobile (no hover). Safe no-op if blocked.
            try {
              if (!v.dataset.lpSeeked) {
                v.dataset.lpSeeked = "1";
                if (typeof v.currentTime === "number") v.currentTime = Math.min(0.05, v.duration || 0.05);
              }
            } catch {}

            // ✅ IMPORTANT: do NOT auto-pause if hover-play is active.
            // Otherwise, hover calls play() but this handler pauses right after data loads.
            if (v?.dataset?.lpHovering === "1") return;

            try {
              v.muted = true;
              const p = v.play?.();
              if (p?.catch) p.catch(() => {});
            } catch {}
          }}
        />
      )}
    </div>
  );
}

function isPublicItem(item) {
  const v = (item?.visibility || "").toString().toUpperCase().trim();
  if (!v) return true; // if missing, assume public
  return v === "PUBLIC";
}


/** Landing-page preview (Pinterest-like masonry) */
/** Landing-page preview (Pinterest-like masonry) */

function HowItWorksSection({ t = (k) => k } = {}) {
  const railRef = useRef(null);

  const steps = useMemo(
    () => [
      { id: "lp-step-1", icon: "bi-journal-richtext", label: t("step_script"), kind: 1 },
      { id: "lp-step-2", icon: "bi-people", label: t("step_invite"), kind: 2 },
      { id: "lp-step-3", icon: "bi-cloud-arrow-up", label: t("step_collect"), kind: 3 },
      { id: "lp-step-4", icon: "bi-magic", label: t("step_edit"), kind: 4 },
      { id: "lp-step-5", icon: "bi-share", label: t("step_export"), kind: 5 },
    ],
    []
  );

  const scrollToStep = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  return (
    <section id="how-it-works" className="lp-how" aria-label={t("how_aria")}>
      <style>{`
      /* Global bold remover on landing */
      .landing-wrapper h1,
      .landing-wrapper h2,
      .landing-wrapper h3,
      .landing-wrapper h4,
      .landing-wrapper h5,
      .landing-wrapper h6,
      .landing-wrapper .lp-sectionTitle,
      .landing-wrapper .lp-heroTitle,
      .landing-wrapper .lp-loginTitle,
      .landing-wrapper .lp-pipeTitle{ font-weight: 200 !important; }
      .landing-wrapper p,
      .landing-wrapper span,
      .landing-wrapper a,
      .landing-wrapper button,
      .landing-wrapper input,
      .landing-wrapper .lp-sectionSub,
      .landing-wrapper .lp-pipeSub{ font-weight: 200 !important; }
      .landing-wrapper strong{ font-weight: 200 !important; }

      /* ============================================================
         ✅ Ergonomic + Stylish refresh (WITHOUT changing your rules):
         - Process panel height = Final video height (same height)
         - Steps stay INLINE + horizontal scroll inside process only
         - Final video NO horizontal scroll
         - Better spacing, visual hierarchy, rail cues, mobile polish
         ============================================================ */

      :root{
        --lp-panel-h: 340px;          /* SAME height for both blocks */
        --lp-gap: 14px;
        --lp-radius: 18px;
        --lp-card-w: 320px;
        --lp-hair: rgba(0,0,0,0.08);
        --lp-ink: rgba(0,0,0,0.82);
        --lp-muted: rgba(0,0,0,0.58);
      }
      @media (max-width: 1200px){
        :root{ --lp-card-w: 310px; }
      }
      @media (max-width: 992px){
        :root{ --lp-panel-h: 330px; --lp-card-w: 300px; }
      }
      @media (max-width: 720px){
        :root{ --lp-panel-h: 320px; --lp-card-w: 290px; }
      }

      /* OUTER: inline blocks, NO horizontal scroll here */
      .lp-processShowcase{
        margin-top: 16px;
        display: flex;
        gap: var(--lp-gap);
        align-items: stretch;      /* ✅ same visual height */
        flex-wrap: nowrap;         /* desktop stays inline */
        overflow: hidden;          /* ✅ never outer x-scroll */
      }
      @media (max-width: 992px){
        .lp-processShowcase{ flex-wrap: wrap; }
      }

      /* Shared "glass" card style */
      .lp-glassCard{
        border-radius: var(--lp-radius);
        overflow: hidden;
        background: rgba(255,255,255,0.86);
        border: 1px solid var(--lp-hair);
        box-shadow: 0 18px 45px rgba(0,0,0,0.10);
        backdrop-filter: blur(10px);
      }
      .lp-glassCard:hover{ box-shadow: 0 22px 60px rgba(0,0,0,0.12); }

      /* Left: process panel */
      .lp-processCard{
        flex: 1 1 560px;
        min-width: 520px;
        height: var(--lp-panel-h);
        display:flex;
        flex-direction: column;
      }
      @media (max-width: 992px){
        .lp-processCard{ min-width: 0; width: 100%; }
      }

      .lp-processHeader{
        padding: 14px 14px 10px;
        display:flex;
        align-items:flex-start;
        justify-content: space-between;
        gap: 12px;
      }
      .lp-processHeader h3{
        margin:0;
        font-size: 15px;
        color: var(--lp-ink);
        letter-spacing: -0.01em;
        font-weight: 520;
      }
      .lp-processHeader p{
        margin: 4px 0 0;
        font-size: 12.5px;
        color: var(--lp-muted);
        line-height: 1.35;
        max-width: 52ch;
      }

      .lp-processRailWrap{
        position: relative;
        padding: 8px 10px 12px;
        height: calc(var(--lp-panel-h) - 74px);
        overflow: hidden;
      }

      /* ✅ Only this inner rail scrolls horizontally */
      .lp-processRail{
        display: flex;
        gap: 12px;
        height: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        padding: 4px 6px 10px;
      }

      .lp-processRail::-webkit-scrollbar{ height: 10px; }
      .lp-processRail::-webkit-scrollbar-thumb{
        background: rgba(0,0,0,0.14);
        border-radius: 999px;
        border: 2px solid rgba(255,255,255,0.7);
      }
      .lp-processRail::-webkit-scrollbar-track{ background: rgba(0,0,0,0.04); border-radius: 999px; }

      /* subtle scroll shadows */
      .lp-processRailWrap::before,
      .lp-processRailWrap::after{
        content:"";
        position:absolute;
        top: 0;
        bottom: 0;
        width: 34px;
        pointer-events:none;
        z-index: 2;
      }
      .lp-processRailWrap::before{
        left: 0;
        background: linear-gradient(90deg, rgba(255,255,255,0.92), rgba(255,255,255,0));
      }
      .lp-processRailWrap::after{
        right: 0;
        background: linear-gradient(270deg, rgba(255,255,255,0.92), rgba(255,255,255,0));
      }

      /* Step card */
      .lp-stepCard{
        scroll-snap-align: start;
        flex: 0 0 var(--lp-card-w);
        height: 100%;
        border-radius: 16px;
        border: 1px solid rgba(0,0,0,0.08);
        background: rgba(255,255,255,0.88);
        box-shadow: 0 10px 28px rgba(0,0,0,0.07);
        overflow:hidden;
        display:flex;
        flex-direction: column;
      }
      .lp-stepTop{
        padding: 10px 12px 8px;
        display:flex;
        align-items:center;
        justify-content: space-between;
        border-bottom: 1px solid rgba(0,0,0,0.06);
      }
      .lp-stepBadge{
        display:inline-flex;
        align-items:center;
        gap: 8px;
        font-size: 12px;
        color: rgba(0,0,0,0.70);
      }
      .lp-stepBadge i{ font-size: 14px; opacity: .9; }

      .lp-stepBtn{
        border: 1px solid rgba(0,0,0,0.10);
        background: rgba(255,255,255,0.70);
        border-radius: 999px;
        padding: 7px 10px;
        font-size: 12px;
        color: rgba(0,0,0,0.72);
        display:inline-flex;
        align-items:center;
        gap: 8px;
        transition: transform .16s ease, background .16s ease, box-shadow .16s ease;
      }
      .lp-stepBtn:hover{
        transform: translateY(-1px);
        background: rgba(255,255,255,0.92);
        box-shadow: 0 10px 22px rgba(0,0,0,0.08);
      }

      .lp-stepBody{
        padding: 10px 10px 12px;
        display:flex;
        align-items:center;
        justify-content:center;
        flex: 1;
      }

      /* Right: final video */
      .lp-processVideoCard{
        flex: 0 0 420px;
        height: var(--lp-panel-h);
        position: relative;
      }
      @media (max-width: 1200px){
        .lp-processVideoCard{ flex: 0 0 400px; }
      }
      @media (max-width: 992px){
        .lp-processVideoCard{ width: 100%; flex: 1 1 100%; }
      }

      .lp-finalVideoWrap{
        position: relative;
        height: 100%;
      }
      .lp-finalVideo{
        width: 100%;
        height: 100%;
        object-fit: cover;
        display:block;
        transform: scale(1.02);
        filter: contrast(1.03) saturate(1.06);
      }
      .lp-finalShade{
        position:absolute;
        inset:0;
        background:
          radial-gradient(600px 280px at 20% 20%, rgba(0,0,0,0.20), transparent 60%),
          linear-gradient(0deg, rgba(0,0,0,0.45), transparent 55%);
        pointer-events:none;
      }
      .lp-finalBadge{
        position:absolute;
        left: 12px;
        bottom: 12px;
        display:inline-flex;
        align-items:center;
        gap: 10px;
        border-radius: 999px;
        padding: 10px 12px;
        background: rgba(255,255,255,0.88);
        border: 1px solid rgba(255,255,255,0.55);
        box-shadow: 0 18px 55px rgba(0,0,0,0.18);
        font-size: 12px;
        color: rgba(0,0,0,0.76);
      }
      .lp-finalBadge i{ font-size: 14px; }

      /* Section title */
      .lp-how .lp-sectionTitle{
        margin: 0;
        font-size: 22px;
        line-height: 1.12;
        letter-spacing: -0.02em;
        color: rgba(0,0,0,0.86);
        font-weight: 520;
      }
      .lp-how .lp-sectionSub{
        margin: 6px 0 0;
        color: rgba(0,0,0,0.58);
        font-size: 13px;
        max-width: 70ch;
      }
      `}</style>


    </section>
  );
}

function UseCaseShowcaseSection({ t = (k) => k } = {}) {
  // Touch terminals: reduce hover-play
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);

  useEffect(() => {
    try {
      const mq = window?.matchMedia?.("(pointer: coarse)");
      const update = () => setIsCoarsePointer(!!mq?.matches);
      update();

      if (mq?.addEventListener) mq.addEventListener("change", update);
      else if (mq?.addListener) mq.addListener(update);

      return () => {
        if (mq?.removeEventListener) mq.removeEventListener("change", update);
        else if (mq?.removeListener) mq.removeListener(update);
      };
    } catch {
      setIsCoarsePointer(false);
      return undefined;
    }
  }, []);


  // Final video previews (Pexels)
  const FINAL_VIDEO_UC1 = "https://videos.pexels.com/video-files/30248342/12967395_1920_1080_60fps.mp4";
  const FINAL_VIDEO_UC2 = "https://videos.pexels.com/video-files/34597291/14661972_3840_2160_24fps.mp4";



  const avatarSvg = useCallback((seed) => {
    const palettes = [
      ["#f7f7ff", "#c7d2fe", "#818cf8"],
      ["#f8fafc", "#bae6fd", "#38bdf8"],
      ["#fffbeb", "#fde68a", "#f59e0b"],
      ["#fdf2f8", "#fbcfe8", "#fb7185"],
      ["#ecfeff", "#a5f3fc", "#06b6d4"],
      ["#f0fdf4", "#bbf7d0", "#22c55e"],
    ];
    const p = palettes[Math.abs(seed) % palettes.length];
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">
        <defs>
          <radialGradient id="g" cx="30%" cy="30%" r="80%">
            <stop offset="0%" stop-color="${p[0]}"/>
            <stop offset="55%" stop-color="${p[1]}"/>
            <stop offset="100%" stop-color="${p[2]}"/>
          </radialGradient>
        </defs>
        <rect width="80" height="80" rx="40" fill="url(#g)"/>
        <circle cx="40" cy="33" r="14" fill="rgba(255,255,255,.72)"/>
        <path d="M18 72c4-16 18-24 22-24s18 8 22 24" fill="rgba(255,255,255,.72)"/>
      </svg>
    `.trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }, []);

  const fakeUsers = useMemo(() => {
    const NAMES = [
      "Maya", "Sami", "Lina", "Noah", "Inès", "Yassine", "Emma", "Rami",
      "Chloé", "Adam", "Nora", "Karim", "Sarah", "Mehdi", "Aya", "Younes",
      "Lea", "Omar", "Nadine", "Walid",
    ];

    const AVATAR_POOL = [
      "https://randomuser.me/api/portraits/women/12.jpg",
      "https://randomuser.me/api/portraits/men/32.jpg",
      "https://randomuser.me/api/portraits/women/45.jpg",
      "https://randomuser.me/api/portraits/men/54.jpg",
      "https://randomuser.me/api/portraits/women/68.jpg",
      "https://randomuser.me/api/portraits/men/15.jpg",
      "https://randomuser.me/api/portraits/women/33.jpg",
      "https://randomuser.me/api/portraits/men/71.jpg",
      "https://randomuser.me/api/portraits/women/26.jpg",
      "https://randomuser.me/api/portraits/men/9.jpg",
      "https://randomuser.me/api/portraits/women/6.jpg",
      "https://randomuser.me/api/portraits/men/22.jpg",
      "https://randomuser.me/api/portraits/women/52.jpg",
      "https://randomuser.me/api/portraits/men/60.jpg",
      "https://randomuser.me/api/portraits/women/75.jpg",
      "https://randomuser.me/api/portraits/men/81.jpg",
      "https://randomuser.me/api/portraits/women/19.jpg",
      "https://randomuser.me/api/portraits/men/44.jpg",
      "https://randomuser.me/api/portraits/women/88.jpg",
      "https://randomuser.me/api/portraits/men/95.jpg",
      "https://randomuser.me/api/portraits/women/91.jpg",
      "https://randomuser.me/api/portraits/men/2.jpg",
    ];

    const hash32 = (str) => {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    };

    const out = [];
    for (let i = 0; i < 18; i++) {
      const name = NAMES[i % NAMES.length];
      const seed = hash32(`uc-${i}-${name}`);
      const img = AVATAR_POOL[seed % AVATAR_POOL.length];
      out.push({
        name: `${name} ${String.fromCharCode(65 + (i % 26))}.`,
        img,
        fallback: avatarSvg(seed),
      });
    }
    return out;
  }, [avatarSvg]);

  const onHoverPlay = useCallback(
    (e) => {
      if (isCoarsePointer) return;
      const root = e.currentTarget;
      const v = root?.querySelector?.("video");
      if (!v) return;

      try {
        v.dataset.lpHovering = "1";
        if (v.preload !== "auto") v.preload = "auto";
        if (v.readyState < 2) v.load();
        v.currentTime = 0;
        v.muted = false;
        v.volume = 1;
      } catch {}

      const p = v.play?.();
      if (p?.catch) p.catch(() => {});
    },
    [isCoarsePointer]
  );

  const onHoverStop = useCallback(
    (e) => {
      if (isCoarsePointer) return;
      const root = e.currentTarget;
      const v = root?.querySelector?.("video");
      if (!v) return;

      try {
        v.dataset.lpHovering = "0";
      } catch {}

      try {
        v.muted = true;
        const p = v.play?.();
        if (p?.catch) p.catch(() => {});
      } catch {}
    },
    [isCoarsePointer]
  );

  const steps = useMemo(
    () => [
      { icon: "bi-journal-richtext", label: t("step_script") },
      { icon: "bi-people", label: t("step_invite") },
      { icon: "bi-cloud-arrow-up", label: t("step_collect") },
      { icon: "bi-magic", label: t("step_edit") },
      { icon: "bi-share", label: t("step_export") },
    ],
    [t]
  );

  return (
    <section id="usecase" className="lp-usecase" aria-label={t("usecase_aria")}>
      <style>{`
        /* ============================
           Pinterest-like Use Cases
           ============================ */
        .lp-usecase{
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 55%, #ffffff 100%);
          position: relative;
        }
        .lp-usecase .lp-ucWrap{
          max-width: 1180px;
          margin: 0 auto;
          padding: 72px 18px 64px;
        }
        .lp-usecase .lp-usecaseHead{
          text-align: center;
          margin-bottom: 26px;
        }
        .lp-usecase .lp-ucKicker{
          display: inline-flex;
          align-items: center;
          gap: .55rem;
          padding: .35rem .8rem;
          border-radius: 999px;
          background: rgba(13,110,253,.10);
          color: #0d6efd;
          font-weight: 700;
          letter-spacing: .02em;
          font-size: .9rem;
        }
        .lp-usecase .lp-usecaseHead h2{
          margin: 14px 0 8px;
          font-weight: 800;
          font-size: clamp(1.6rem, 3vw, 2.25rem);
          color: #0f172a;
        }
        .lp-usecase .lp-usecaseHead p{
          margin: 0 auto;
          max-width: 78ch;
          color: rgba(15,23,42,.72);
          font-size: 1.02rem;
          line-height: 1.55;
        }

        .lp-ucLines{
          display: flex;
          flex-direction: column;
          gap: 18px;
          margin-top: 28px;
        }

        .lp-ucLine{
          background: rgba(255,255,255,.90);
          border: 1px solid rgba(15,23,42,.08);
          border-radius: 18px;
          box-shadow: 0 10px 30px rgba(15,23,42,.06);
          overflow: hidden;
        }
        .lp-ucLineInner{
          display: grid;
          grid-template-columns: 360px 1fr;
          gap: 18px;
          padding: 18px;
        }

        .lp-ucInfo{
          padding: 6px 4px 6px 6px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-width: 0;
        }
        .lp-ucCasePill{
          display: inline-flex;
          align-items: center;
          gap: .45rem;
          padding: .28rem .65rem;
          border-radius: 999px;
          background: rgba(15,23,42,.06);
          color: rgba(15,23,42,.8);
          font-weight: 700;
          font-size: .78rem;
          width: fit-content;
        }
        .lp-ucInfo h3{
          margin: 0;
          font-weight: 800;
          font-size: 1.28rem;
          color: #0f172a;
        }
        .lp-ucInfo p{
          margin: 0;
          color: rgba(15,23,42,.72);
          line-height: 1.5;
          font-size: .98rem;
        }
        .lp-ucBullets{
          margin: 6px 0 0;
          padding-left: 1.1rem;
          color: rgba(15,23,42,.72);
          font-size: .95rem;
          line-height: 1.45;
        }
        .lp-ucBullets li{ margin: 6px 0; }

        .lp-ucMiniSteps{
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }
        .lp-ucChip{
          display: inline-flex;
          align-items: center;
          gap: .45rem;
          padding: .36rem .6rem;
          border-radius: 999px;
          border: 1px solid rgba(15,23,42,.10);
          background: rgba(248,250,252,.75);
          color: rgba(15,23,42,.78);
          font-weight: 700;
          font-size: .80rem;
          white-space: nowrap;
        }
        .lp-ucChip i{ color: rgba(15,23,42,.55); }

        .lp-ucFlow{
          display: grid;
          grid-template-columns: 1.15fr 52px 1.35fr;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }
        .lp-ucCol{
          min-width: 0;
        }
        .lp-ucColHead{
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 10px;
          margin-bottom: 10px;
        }
        .lp-ucColHead .ttl{
          font-weight: 800;
          color: #0f172a;
          font-size: .86rem;
        }
        .lp-ucColHead .sub{
          color: rgba(15,23,42,.6);
          font-size: .78rem;
          white-space: nowrap;
        }

        /* Masonry “pins” */
        .lp-pinMasonry{
          column-count: 3;
          column-gap: 10px;
        }
        .lp-pin{
          break-inside: avoid;
          margin: 0 0 10px;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid rgba(15,23,42,.10);
          background: #fff;
          box-shadow: 0 8px 18px rgba(15,23,42,.08);
          transform: translateY(0);
          transition: transform .18s ease, box-shadow .18s ease;
          position: relative;
        }
        .lp-pin:hover{
          transform: translateY(-2px);
          box-shadow: 0 14px 28px rgba(15,23,42,.12);
        }
        .lp-pinMedia{
          width: 100%;
          height: auto;
          display: block;
        }
        .lp-pin video.lp-pinMedia{
          width: 100%;
          height: auto;
          display: block;
        }
        .lp-pinBadge{
          position: absolute;
          left: 10px;
          bottom: 10px;
          padding: .22rem .5rem;
          border-radius: 999px;
          background: rgba(15,23,42,.72);
          color: #fff;
          font-weight: 800;
          font-size: .78rem;
          display: inline-flex;
          align-items: center;
          gap: .35rem;
          backdrop-filter: blur(6px);
        }

        .lp-ucArrow{
          display: grid;
          place-items: center;
          width: 52px;
          height: 52px;
          border-radius: 999px;
          border: 1px solid rgba(15,23,42,.10);
          background: rgba(255,255,255,.85);
          box-shadow: 0 10px 20px rgba(15,23,42,.08);
          color: rgba(15,23,42,.70);
        }
        .lp-ucArrow i{ font-size: 1.15rem; }

        .lp-finalCard{
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(15,23,42,.10);
          background: #0b1220;
          box-shadow: 0 12px 26px rgba(15,23,42,.12);
        }
        .lp-finalPlayer{
          position: relative;
          aspect-ratio: 16/9;
          background: #0b1220;
        }
        .lp-finalPlayer video{
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .lp-finalOverlay{
          position: absolute;
          inset: 0;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 10px;
          padding: 10px;
          pointer-events: none;
        }
        .lp-finalOverlay .tag{
          padding: .25rem .6rem;
          border-radius: 999px;
          background: rgba(255,255,255,.16);
          color: rgba(255,255,255,.92);
          font-weight: 800;
          font-size: .8rem;
          backdrop-filter: blur(8px);
        }
        .lp-finalOverlay .cta{
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          padding: .42rem .75rem;
          border-radius: 999px;
          background: rgba(13,110,253,.92);
          color: #fff;
          font-weight: 800;
          font-size: .86rem;
          box-shadow: 0 12px 26px rgba(13,110,253,.28);
        }

        .lp-finalFooter{
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          padding: 10px 12px 12px;
          background: rgba(255,255,255,.92);
        }
        .lp-miniBadges{
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        .lp-miniBadge{
          display: inline-flex;
          align-items: center;
          gap: .45rem;
          padding: .32rem .55rem;
          border-radius: 999px;
          background: rgba(15,23,42,.06);
          color: rgba(15,23,42,.78);
          font-weight: 800;
          font-size: .76rem;
        }
        .lp-miniBadge i{ color: rgba(15,23,42,.55); }

        .lp-creditsRow{
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 10px;
          color: rgba(15,23,42,.72);
          font-weight: 700;
          font-size: .92rem;
        }
        .lp-creditsAvatars{
          display: flex;
          align-items: center;
        }
        .lp-creditsAvatars img{
          width: 28px;
          height: 28px;
          border-radius: 999px;
          object-fit: cover;
          border: 2px solid #fff;
          box-shadow: 0 6px 14px rgba(15,23,42,.14);
          margin-left: -8px;
        }
        .lp-creditsAvatars img:first-child{ margin-left: 0; }

        .lp-ucCTAbar{
          display: flex;
          justify-content: center;
          margin-top: 22px;
        }

        @media (max-width: 1080px){
          .lp-ucLineInner{ grid-template-columns: 1fr; }
          .lp-ucFlow{ grid-template-columns: 1fr; gap: 12px; }
          .lp-ucArrow{ display: none; }
        }
        @media (max-width: 720px){
          .lp-usecase .lp-ucWrap{ padding: 58px 14px 54px; }
          .lp-pinMasonry{ column-count: 2; }
        }
        @media (max-width: 420px){
          .lp-pinMasonry{ column-count: 1; }
        }
      `}</style>

      <div className="lp-ucWrap">
        <div className="lp-usecaseHead">
          <h2>{t("usecase_title")}</h2>
          <p>{t("usecase_sub")}</p>
        </div>

        {/* One use case per line */}
        <div className="lp-ucLines" aria-label={t("usecase_templates_aria")}>
          {/* ====== USE CASE 1 ====== */}
          <div className="lp-ucLine">
            <div className="lp-ucLineInner">
              <div className="lp-ucInfo">
                
                <h3>{t("usecase_tpl1_title")}</h3>
                <p>{t("usecase_tpl1_sub")}</p>

                <ul className="lp-ucBullets">
                  <li>{t("usecase_tpl1_b1")}</li>
                  <li>{t("usecase_tpl1_b2")}</li>
                  <li>{t("usecase_tpl1_b3")}</li>
                </ul>

                <div className="lp-ucMiniSteps">
                  {steps.slice(1, 4).map((s) => (
                    <span key={s.icon} className="lp-ucChip">
                      <i className={`bi ${s.icon}`} />
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="lp-ucFlow">
                {/* Contributions */}
                <div className="lp-ucCol lp-ucCol--contrib">
                  <div className="lp-ucColHead">
                    <div className="ttl">{t("usecase_left_title")}</div>
                    <div className="sub">{t("usecase_zoo_hint")}</div>
                  </div>

                  <div className="lp-pinMasonry">
                    {[
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/615277/pexels-photo-615277.jpeg?cs=srgb&dl=pexels-goran-vrakela-64248-615277.jpg&fm=jpg",
                        label: t("usecase_zoo_clip3"),
                        icon: "bi-camera-video",
                      },
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/7299/africa-animals-zoo-zebras.jpg?cs=srgb&dl=pexels-creative-vix-7299.jpg&fm=jpg",
                        label: t("usecase_zoo_clip1"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/33733373/pexels-photo-33733373.jpeg?cs=srgb&dl=pexels-denissevillar-33733373.jpg&fm=jpg",
                        label: t("usecase_zoo_clip2"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/2698880/pexels-photo-2698880.jpeg?cs=srgb&dl=pexels-livvux-2698880.jpg&fm=jpg",
                        label: t("usecase_zoo_clip2"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "vid",
                        src: "https://videos.pexels.com/video-files/32260719/13758503_1080_1920_60fps.mp4",
                        label: t("usecase_zoo_clip1"),
                        icon: "bi-play-fill",
                      },
                      {
                        kind: "vid",
                        src: "https://videos.pexels.com/video-files/29672422/12763529_1080_1920_30fps.mp4",
                        label: t("usecase_zoo_clip3"),
                        icon: "bi-play-fill",
                      },
                    ].map((pin, i) => (
                      <div
                        key={`zoo-pin-${i}`}
                        className="lp-pin"
                        onMouseEnter={pin.kind === "vid" ? onHoverPlay : undefined}
                        onMouseLeave={pin.kind === "vid" ? onHoverStop : undefined}
                      >
                        {pin.kind === "img" ? (
                          <img
                            className="lp-pinMedia"
                            src={pin.src}
                            alt={pin.label}
                            loading="lazy"
                          />
                        ) : (
                          <video
                            className="lp-pinMedia"
                            src={pin.src}
                            muted
                            playsInline
                            loop
                            preload="metadata"
                          />
                        )}
                        <span className="lp-pinBadge">
                          <i className={`bi ${pin.icon}`} />
                          {pin.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Arrow */}
                <div className="lp-ucArrow" aria-hidden="true">
                  <i className="bi bi-arrow-right" />
                </div>

                {/* Final */}
                <div className="lp-ucCol lp-ucCol--final"><div className="lp-finalCard">
                    <div className="lp-finalPlayer" onMouseEnter={onHoverPlay} onMouseLeave={onHoverStop}>
                      <video
                        src={FINAL_VIDEO_UC1}
                        muted
                        playsInline
                        loop
                        preload="metadata"
                      />
                      
                    </div>

                    <div className="lp-finalFooter">
                      <div className="lp-miniBadges">
                        <span className="lp-miniBadge">
                          <i className="bi bi-mic" />
                          {t("usecase_footer_vo")}
                        </span>
                        <span className="lp-miniBadge">
                          <i className="bi bi-music-note-beamed" />
                          {t("usecase_footer_music")}
                        </span>
                        <span className="lp-miniBadge">
                          <i className="bi bi-shuffle" />
                          {t("usecase_footer_transitions")}
                        </span>
                      </div>

                      <span className="lp-ucChip">
                        <i className="bi bi-award" />
                        {t("usecase_board_zoo_chip")}
                      </span>
                    </div>
                  </div>

                  <div className="lp-creditsRow">
                    <div className="lp-creditsAvatars" aria-hidden="true">
                      {fakeUsers.slice(0, 6).map((u, idx) => (
                        <img
                          key={`uc1-av-${idx}`}
                          src={u.img}
                          alt=""
                          onError={(e) => (e.currentTarget.src = u.fallback)}
                          loading="lazy"
                        />
                      ))}
                    </div>
                    <span>
                      <i className="bi bi-check2-circle" /> {t("usecase_credit")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ====== USE CASE 2 ====== */}
          <div className="lp-ucLine">
            <div className="lp-ucLineInner">
              <div className="lp-ucInfo">
                
                <h3>{t("usecase_tpl2_title")}</h3>
                <p>{t("usecase_tpl2_sub")}</p>

                <ul className="lp-ucBullets">
                  <li>{t("usecase_tpl2_b1")}</li>
                  <li>{t("usecase_tpl2_b2")}</li>
                  <li>{t("usecase_tpl2_b3")}</li>
                </ul>

                <div className="lp-ucMiniSteps">
                  {steps.slice(1, 4).map((s) => (
                    <span key={`w-${s.icon}`} className="lp-ucChip">
                      <i className={`bi ${s.icon}`} />
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="lp-ucFlow">
                {/* Contributions */}
                <div className="lp-ucCol lp-ucCol--contrib">
                  <div className="lp-ucColHead">
                    <div className="ttl">{t("usecase_left_title")}</div>
                    <div className="sub">{t("usecase_world_hint")}</div>
                  </div>

                  <div className="lp-pinMasonry">
                    {[
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/31379831/pexels-photo-31379831.jpeg?cs=srgb&dl=pexels-hoang-vu-257779885-31379831.jpg&fm=jpg",
                        label: t("usecase_world_clip1"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/9828298/pexels-photo-9828298.jpeg?cs=srgb&dl=pexels-sonya-livshits-113472440-9828298.jpg&fm=jpg",
                        label: t("usecase_world_clip1"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/31469976/pexels-photo-31469976.jpeg?cs=srgb&dl=pexels-jordicosta-31469976.jpg&fm=jpg",
                        label: t("usecase_world_clip2"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "img",
                        src: "https://images.pexels.com/photos/29034983/pexels-photo-29034983.jpeg?cs=srgb&dl=pexels-triemli-29034983.jpg&fm=jpg",
                        label: t("usecase_world_clip3"),
                        icon: "bi-camera",
                      },
                      {
                        kind: "vid",
                        src: "https://videos.pexels.com/video-files/5775824/5775824-hd_1080_1920_30fps.mp4",
                        label: t("usecase_world_clip1"),
                        icon: "bi-play-fill",
                      },
                      {
                        kind: "vid",
                        src: "https://videos.pexels.com/video-files/32520145/13867640_1080_1920_24fps.mp4",
                        label: t("usecase_world_clip2"),
                        icon: "bi-play-fill",
                      },
                    ].map((pin, i) => (
                      <div
                        key={`world-pin-${i}`}
                        className="lp-pin"
                        onMouseEnter={pin.kind === "vid" ? onHoverPlay : undefined}
                        onMouseLeave={pin.kind === "vid" ? onHoverStop : undefined}
                      >
                        {pin.kind === "img" ? (
                          <img
                            className="lp-pinMedia"
                            src={pin.src}
                            alt={pin.label}
                            loading="lazy"
                          />
                        ) : (
                          <video
                            className="lp-pinMedia"
                            src={pin.src}
                            muted
                            playsInline
                            loop
                            preload="metadata"
                          />
                        )}
                        <span className="lp-pinBadge">
                          <i className={`bi ${pin.icon}`} />
                          {pin.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Arrow */}
                <div className="lp-ucArrow" aria-hidden="true">
                  <i className="bi bi-arrow-right" />
                </div>

                {/* Final */}
                <div className="lp-ucCol lp-ucCol--final"><div className="lp-finalCard">
                    <div className="lp-finalPlayer" onMouseEnter={onHoverPlay} onMouseLeave={onHoverStop}>
                      <video
                        src={FINAL_VIDEO_UC2}
                        muted
                        playsInline
                        loop
                        preload="metadata"
                      />
                     
                    </div>

                    <div className="lp-finalFooter">
                      <div className="lp-miniBadges">
                        <span className="lp-miniBadge">
                          <i className="bi bi-mic" />
                          {t("usecase_footer_vo")}
                        </span>
                        <span className="lp-miniBadge">
                          <i className="bi bi-music-note-beamed" />
                          {t("usecase_footer_music")}
                        </span>
                        <span className="lp-miniBadge">
                          <i className="bi bi-shuffle" />
                          {t("usecase_footer_transitions")}
                        </span>
                      </div>

                      <span className="lp-ucChip">
                        <i className="bi bi-people" />
                        {t("usecase_board_world_chip")}
                      </span>
                    </div>
                  </div>

                  <div className="lp-creditsRow">
                    <div className="lp-creditsAvatars" aria-hidden="true">
                      {fakeUsers.slice(6, 12).map((u, idx) => (
                        <img
                          key={`uc2-av-${idx}`}
                          src={u.img}
                          alt=""
                          onError={(e) => (e.currentTarget.src = u.fallback)}
                          loading="lazy"
                        />
                      ))}
                    </div>
                    <span>
                      <i className="bi bi-check2-circle" /> {t("usecase_credit")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lp-ucCTAbar">
          <button
            type="button"
            className="btn btn-weave"
            onClick={() => {
              try {
                const el = document.getElementById("workflow");
                el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
              } catch {}
            }}
          >
            <i className="bi bi-compass" />
            {t("btn_workflow")}
          </button>
        </div>
      </div>
    </section>
  );}





/* =========================================================
   Pinterest-like "Get your next" carousel sections
   (Creators: image left, text right)
   (Weavers:  image right, text left)
   ========================================================= */

const LP_PUBLIC_BASE = (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) ? process.env.PUBLIC_URL : "";

const LP_GYC_CREATOR_IMGS = [
  `${LP_PUBLIC_BASE}/landing/globalimpact.png`,          // Global Perspectives
  `${LP_PUBLIC_BASE}/landing/massiveProduction.jpeg`,    // Massive Production
  `${LP_PUBLIC_BASE}/landing/SmartSelection.png`,     // Smart Selection
  `${LP_PUBLIC_BASE}/landing/AIenhancedvisbility.jpeg`, // The Power of Truth
  `${LP_PUBLIC_BASE}/landing/Pureinspiration.jpeg`,      // Pure Inspiration
];

const LP_GYC_WEAVER_IMGS = [
  `${LP_PUBLIC_BASE}/landing/globalimpact.png`,          // Global Impact
  `${LP_PUBLIC_BASE}/landing/Pureinspiration.jpeg`,      // Artistic Legacy
  `${LP_PUBLIC_BASE}/landing/instantcollaboration.jpeg`, // Instant Collaboration
  `${LP_PUBLIC_BASE}/landing/AIenhancedvisbility.jpeg`, // AI-Enhanced Visibility
  `${LP_PUBLIC_BASE}/landing/limitlessinspiration.jpeg`, // Limitless Inspiration
];

function lpSvgFallback(seed = "weavevo") {
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="1600" height="1000" viewBox="0 0 1600 1000">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#0b1220"/>
        <stop offset="1" stop-color="#1b2a4a"/>
      </linearGradient>
      <radialGradient id="r" cx="35%" cy="35%" r="70%">
        <stop offset="0" stop-color="#7c3aed" stop-opacity=".55"/>
        <stop offset="1" stop-color="#0b1220" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <rect width="1600" height="1000" fill="url(#g)"/>
    <circle cx="520" cy="340" r="520" fill="url(#r)"/>
    <text x="80" y="880" font-family="ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto" font-size="44" fill="rgba(255,255,255,.70)">${seed}</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function GetYourNextSection({
  id,
  ariaLabel,
  kickerText,
  title,
  sub,
  slides = [],
  reverse = false,
  variant = "creators",
  t = (k) => k,
  autoAdvanceDir = 1,
} = {}) {
  const total = slides.length || 1;
  const [index, setIndex] = useState(0);
  const pauseRef = useRef(false);

  const goTo = useCallback(
    (i) => {
      const n = total || 1;
      const next = ((i % n) + n) % n;
      setIndex(next);
    },
    [total]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-advance (respects reduced-motion)
  useEffect(() => {
    if (total <= 1) return;

    let reduced = false;
    try {
      reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    } catch (e) {
      reduced = false;
    }
    if (reduced) return;

    const timer = window.setInterval(() => {
      if (pauseRef.current) return;
      setIndex((x) => ((x + (autoAdvanceDir || 1)) % total + total) % total);
    }, 5200);

    return () => window.clearInterval(timer);
  }, [total, autoAdvanceDir]);

  // Guard if slides count changes
  useEffect(() => {
    if (index >= total) setIndex(0);
  }, [index, total]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  return (
    <section
      id={id}
      className={`lp-gycSection lp-gycSection--${variant}`}
      aria-label={ariaLabel}
    >
      <div className="container lp-gycContainer">
        <div className="lp-gycHead">
          <div className="lp-gycSectionHeader">{kickerText}</div>
        <h2 className="lp-gycTitle lp-gycTitle--bold">{title}</h2>
<p className="lp-gycSub lp-gycSub--bold">{sub}</p>
        </div>

        <div
          className={`lp-gycShell ${reverse ? "lp-gycShell--reverse" : ""}`}
          role="group"
          aria-roledescription="carousel"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={() => (pauseRef.current = true)}
          onMouseLeave={() => (pauseRef.current = false)}
          onFocus={() => (pauseRef.current = true)}
          onBlur={() => (pauseRef.current = false)}
        >
          <div
            className="lp-gycTrack"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {slides.map((s, i) => (
              <div className="lp-gycSlide" key={s.id || i} aria-hidden={i !== index}>
                <div className="lp-gycSlideRow">
                  <div className="lp-gycImgPane">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = lpSvgFallback(s.title || "weavevo");
                      }}
                    />
                    <div className="lp-gycImgShade" />
</div>

                  <div className="lp-gycTextPane">
                    <h3 className="lp-gycSlideTitle">{s.title}</h3>
                    <p className="lp-gycSlideText">{s.text}</p>
</div>
                </div>
              </div>
            ))}
          </div>

          {total > 1 && (
            <>
              <button
                type="button"
                className="lp-gycNav lp-gycNav--prev"
                onClick={prev}
                aria-label={t("gyc_prev")}
              >
                <i className="bi bi-chevron-left" />
              </button>
              <button
                type="button"
                className="lp-gycNav lp-gycNav--next"
                onClick={next}
                aria-label={t("gyc_next")}
              >
                <i className="bi bi-chevron-right" />
              </button>
            </>
          )}
        </div>

        {total > 1 && (
          <div className="lp-gycDots" aria-label={t("gyc_dots_aria")}>
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`lp-gycDot ${i === index ? "is-active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={t("gyc_goto", { n: i + 1 })}
                aria-current={i === index ? "true" : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CreatorsValueSection({ t = (k) => k } = {}) {
  const slides = useMemo(
    () => [
      {
        id: "c1",
        img: LP_GYC_CREATOR_IMGS[0],
        title: t("creators_slide1_title"),
        text: t("creators_slide1_text"),
      },
      {
        id: "c2",
        img: LP_GYC_CREATOR_IMGS[1],
        title: t("creators_slide2_title"),
        text: t("creators_slide2_text"),
      },
      {
        id: "c3",
        img: LP_GYC_CREATOR_IMGS[2],
        title: t("creators_slide3_title"),
        text: t("creators_slide3_text"),
      },
      {
        id: "c4",
        img: LP_GYC_CREATOR_IMGS[3],
        title: t("creators_slide4_title"),
        text: t("creators_slide4_text"),
      },
      {
        id: "c5",
        img: LP_GYC_CREATOR_IMGS[4],
        title: t("creators_slide5_title"),
        text: t("creators_slide5_text"),
      },
    ],
    [t]
  );

  return (
    <GetYourNextSection
      id="for-creators"
      ariaLabel={t("creators_aria")}
      kickerText={t("creators_kicker")}
      kickerIcon="bi bi-magic"
      title={t("creators_title")}
      sub={t("creators_sub")}
      slides={slides}
      reverse={false}
      variant="creators"
      t={t}
    />
  );
}

function WeaversCommunitySection({ t = (k) => k } = {}) {
  const slides = useMemo(
    () => [
      {
        id: "w1",
        img: LP_GYC_WEAVER_IMGS[0],
        title: t("weavers_slide1_title"),
        text: t("weavers_slide1_text"),
      },
      {
        id: "w2",
        img: LP_GYC_WEAVER_IMGS[1],
        title: t("weavers_slide2_title"),
        text: t("weavers_slide2_text"),
      },
      {
        id: "w3",
        img: LP_GYC_WEAVER_IMGS[2],
        title: t("weavers_slide3_title"),
        text: t("weavers_slide3_text"),
      },
      {
        id: "w4",
        img: LP_GYC_WEAVER_IMGS[3],
        title: t("weavers_slide4_title"),
        text: t("weavers_slide4_text"),
      },
      {
        id: "w5",
        img: LP_GYC_WEAVER_IMGS[4],
        title: t("weavers_slide5_title"),
        text: t("weavers_slide5_text"),
      },
    ],
    [t]
  );

  return (
    <GetYourNextSection
      id="for-weavers"
      ariaLabel={t("weavers_aria")}
      kickerText={t("weavers_kicker")}
      kickerIcon="bi bi-people"
      title={t("weavers_title")}
      sub={t("weavers_sub")}
      slides={slides}
      reverse={true}
      autoAdvanceDir={-1}
      variant="weavers"
      t={t}
    />
  );
}


function LandingPage() {
  const { keycloak } = useAuth();

  // Language: default EN. Settings page can set localStorage(LP_LANG_KEY) to "fr".
  const [lang, setLang] = useState(() => {
    try {
      const raw =
        localStorage.getItem(LP_LANG_KEY) ||
        localStorage.getItem("weavevo_language") ||
        "";
      const norm = String(raw || "").toLowerCase().slice(0, 2);
      return norm === "fr" ? "fr" : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    const read = () => {
      try {
        const raw =
          localStorage.getItem(LP_LANG_KEY) ||
          localStorage.getItem("weavevo_language") ||
          "";
        const norm = String(raw || "").toLowerCase().slice(0, 2);
        const next = norm === "fr" ? "fr" : "en";
        setLang(next);
        if (!raw) localStorage.setItem(LP_LANG_KEY, next);
      } catch {}
    };

    read();
    window.addEventListener("storage", read);
    window.addEventListener("focus", read);

    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("focus", read);
    };
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = LP_I18N?.[lang] || LP_I18N?.en || {};
      let out = dict[key] ?? LP_I18N?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [lang]
  );


  return (
    <div className="landing-wrapper">
      <HeaderRunwayStyle />

      {/* HERO */}
      <section className="lp-hero">
        <video autoPlay muted loop playsInline className="lp-heroVideo">
          <source src={demoVideo} type="video/mp4" />
        </video>

        {/* =========================
    HERO SECTION (UPDATED)
    - text: direct + precise + vendeur
    - style: smaller, no bold, more stylish (scoped)
   ========================= */}
        <div className="lp-heroOverlay">
          <style>{`
    /* Scoped: only this hero overlay (smaller + clean + no bold) */
    .lp-heroOverlay .lp-heroInner{ padding: 24px 14px; }

  .lp-heroOverlay{
  background: linear-gradient(
    180deg,
    rgba(0,0,0,.26) 0%,
    rgba(0,0,0,.44) 55%,
    rgba(0,0,0,.60) 100%
  );
}

.lp-heroOverlay .lp-heroTitle{ font-weight: 860; }
.lp-heroOverlay .lp-heroSub{ font-weight: 560; }


        .lp-heroOverlay .lp-heroLeft{ max-width: 660px; }

    /* ✅ Kicker pill (like screenshot) */
    .lp-heroOverlay .lp-heroKicker{
      width: 100%;
      display:flex;
      align-items:center;
      gap:10px;
      padding: 10px 14px;
      border-radius: 999px;
      background: rgba(255,255,255,.055);
      border: 1px solid rgba(255,255,255,.13);
      box-shadow: 0 16px 46px rgba(0,0,0,.16);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      font-size:11.8px;
      letter-spacing:.02em;
      opacity:.98;
      margin-bottom: 12px;
      color: rgba(255,255,255,.88);
      text-shadow: 0 2px 14px rgba(0,0,0,.35);
    }
    .lp-heroOverlay .lp-heroKickerIco{
      width: 26px;
      height: 26px;
      display:flex;
      align-items:center;
      justify-content:center;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,.16);
      background: rgba(255,255,255,.06);
      box-shadow: inset 0 1px 0 rgba(255,255,255,.10);
      flex: 0 0 auto;
    }
    .lp-heroOverlay .lp-heroKickerIco i{ font-size:14px; opacity:.92; }
    .lp-heroOverlay .lp-heroKickerTxt{ font-weight:480; opacity:.96; }

    /* ✅ Panel wrapping CTAs (no frame) */
    .lp-heroOverlay .lp-heroPanel{
      margin-top: 16px;
      padding: 0;
      border-radius: 0;
      background: transparent;
      border: 0;
      box-shadow: none;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .lp-heroOverlay .lp-heroBullets{
      display:flex;
      flex-direction:column;
      gap:10px;
      margin: 0;
    }

    .lp-heroOverlay .lp-bullet{
      position: relative;
      display:flex;
      align-items:center;
      gap:12px;
      width: 100%;
      padding: 12px 14px;
      border-radius: 16px;
      background: rgba(255,255,255,.05);
      border: 1px solid rgba(255,255,255,.12);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      box-shadow: 0 16px 46px rgba(0,0,0,.14);
      overflow:hidden;
    }
    .lp-heroOverlay .lp-bullet::before{
      content:"";
      position:absolute;
      inset:-2px;
      background:
        radial-gradient(220px 140px at 22% 24%, rgba(99,102,241,.22), transparent 66%),
        radial-gradient(220px 140px at 86% 80%, rgba(56,189,248,.14), transparent 68%);
      opacity:.55;
      pointer-events:none;
    }
    .lp-heroOverlay .lp-bullet > *{ position: relative; z-index: 1; }

    .lp-heroOverlay .lp-bulletIco{
      width: 28px;
      height: 28px;
      border-radius: 999px;
      display:flex;
      align-items:center;
      justify-content:center;
      background: rgba(255,255,255,.06);
      border: 1px solid rgba(255,255,255,.14);
      box-shadow: inset 0 1px 0 rgba(255,255,255,.10);
      flex: 0 0 auto;
    }
    .lp-heroOverlay .lp-bulletIco i{ font-size:15px; opacity:.95; }

    .lp-heroOverlay .lp-bulletTxt{
      font-size:12.8px;
      line-height:1.35;
      font-weight:420;
      opacity:.96;
    }

    .lp-heroOverlay .lp-heroActions{
      display:flex;
      align-items:center;
      gap:10px;
      margin-top: 12px;
      flex-wrap: nowrap;
    }
    .lp-heroOverlay .lp-heroActions .lp-btn{
      padding: 10.5px 14px;
      font-size: 12.8px;
      border-radius: 16px;
      font-weight: 520;            /* ✅ not bold */
      letter-spacing: .01em;
      transition: transform .15s ease, filter .15s ease, opacity .15s ease;
      will-change: transform;
      display:inline-flex;
      align-items:center;
      gap:10px;
      white-space: nowrap;
    }
    .lp-heroOverlay .lp-heroActions .lp-btn:hover{
      transform: translateY(-1px);
      filter: brightness(1.03);
    }

    /* Make the "Start" CTA stand out (primary) */
    .lp-heroOverlay .lp-btnPrimary--hero{
      background: rgba(255,255,255,.94);
      color: rgba(0,0,0,.80);
      border: 1px solid rgba(255,255,255,.72);
      box-shadow: 0 22px 70px rgba(0,0,0,.22);
    }
    .lp-heroOverlay .lp-btnPrimary--hero i{ color: rgba(0,0,0,.70); }

    /* Secondary CTA: glass but prominent */
    .lp-heroOverlay .lp-btnSecondary--hero{
      background: rgba(255,255,255,.08);
      border: 1px solid rgba(255,255,255,.16);
      color: rgba(255,255,255,.92);
      box-shadow: 0 18px 55px rgba(0,0,0,.16);
    }
    .lp-heroOverlay .lp-btnSecondary--hero:hover{
      background: rgba(255,255,255,.12);
      border-color: rgba(255,255,255,.22);
    }
    .lp-heroOverlay .lp-btnSecondary--hero i{ color: rgba(255,255,255,.88); }

    /* Keep "workflow" CTA glassy */
    .lp-heroOverlay .lp-btnGhostOnDark{
      background: rgba(255,255,255,.06);
      border: 1px solid rgba(255,255,255,.12);
    }
    .lp-heroOverlay .lp-btnPrimary{
      box-shadow: 0 18px 55px rgba(0,0,0,.16);
    }

    @media (max-width: 720px){
      .lp-heroOverlay .lp-heroActions{ flex-wrap: wrap; }
      .lp-heroOverlay .lp-heroActions .lp-btn{ width: 100%; justify-content: center; }
      .lp-heroOverlay .lp-heroActions .lp-btnSecondary--hero{ margin-left: 0; }
    }

    /* Right panel: smaller + readable text */
    .lp-heroOverlay .lp-heroRight{ max-width: 420px; width: 100%; }

    .lp-heroOverlay .lp-loginCard{
      border-radius: 18px;
      background: rgba(255,255,255,.07);
      border: 1px solid rgba(255,255,255,.12);
      box-shadow: 0 18px 44px rgba(0,0,0,.16);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      overflow: hidden;
    }

    .lp-heroOverlay .lp-loginHead{
      text-align: center;
      padding: 14px 14px 10px;
      border-bottom: 1px solid rgba(255,255,255,.10);
      color: rgba(255,255,255,.94);     /* ✅ force visibility */
      text-shadow: 0 2px 12px rgba(0,0,0,.28);
    }
    .lp-heroOverlay .lp-loginTitle{
      display:flex;
      align-items:center;
      gap:10px;
      font-size: 13.5px;
      font-weight: 520;                 /* ✅ not bold */
      opacity: 1;
      color: rgba(255,255,255,.96);     /* ✅ visible */
    }
    .lp-heroOverlay .lp-loginSub{
      text-align: center;
      margin-top: 0px;
      font-size: clamp(16px, 1.3vw, 18px);
      letter-spacing: -0.01em;
      opacity: 1;
      line-height: 1.25;
      color: rgba(255,255,255,.94);     /* ✅ visible */
    }

    .lp-heroOverlay .lp-loginNote{
      display:flex;
      align-items:center;
      gap:10px;
      margin-top: 10px;
      font-size: 12px;
      font-weight: 420;
      opacity: .88;
      color: rgba(255,255,255,.86);
      text-shadow: 0 2px 12px rgba(0,0,0,.28);
    }
    .lp-heroOverlay .lp-loginNote i{ opacity:.92; }

    /* Welcome note (no framed info box) */
    .lp-heroOverlay .lp-loginNote--welcome{
      gap: 0;
      padding: 0;
      border: 0;
      background: transparent;
      box-shadow: none;
      font-weight: 650;
      opacity: .95;
      color: rgba(255,255,255,.92);
    }


    @media (max-width: 992px){
      .lp-heroOverlay .lp-heroInner{ padding: 20px 12px; }
      .lp-heroOverlay .lp-heroTitle{ font-size: clamp(24px, 7vw, 34px); }
      .lp-heroOverlay .lp-heroRight{ max-width: 100%; margin-top: 14px; }
    }
  `}</style>

          <div className="container lp-heroInner">
            {/* Left: message */}
            <div className="lp-heroLeft">
           

              <h1 className="lp-heroTitle">
                {t("hero_title_line1")}
                <br />
                {t("hero_title_line2")}
              </h1>

              <p className="lp-heroSub">{t("hero_subheadline")}</p>

              <div className="lp-heroPanel">
             

                
<div className="lp-heroCtaBar" role="group" aria-label="Hero actions">
                  <button
                    type="button"
                    className="lp-ctaBtn lp-ctaBtn--soft"
                    onClick={() => keycloak?.login?.()}
                  >
                    <span className="lp-ctaIcon" aria-hidden="true">
                      <i className="bi bi-box-arrow-in-right" />
                    </span>
                    <span className="lp-ctaLabel">{t("btn_start")}</span>
                  </button>

                  <a href="#for-weavers" className="lp-ctaBtn lp-ctaBtn--teal">
                    <span className="lp-ctaIcon" aria-hidden="true">
                      <i className="bi bi-compass" />
                    </span>
                    <span className="lp-ctaLabel">{t("btn_discover")}</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: login */}
            <div className="lp-heroRight">
              <div className="lp-loginCard">
                <div className="lp-loginHead">
                  
                  <div className="lp-loginSub">
                    {t("login_sub")}
                  </div>
                </div>

                <CustomLoginForm />
              </div>

              
            </div>
          </div>
        </div>

      </section>

      {/* FOR CREATORS */}
      <CreatorsValueSection t={t} />

      {/* FOR WEAVERS */}
      <WeaversCommunitySection t={t} />

      {/* HOW IT WORKS */}
      <HowItWorksSection t={t} />

      {/* USE CASE SHOWCASE */}
      <UseCaseShowcaseSection t={t} />

      {/* PIPELINE removed */}
{/* CTA */}
      <section className="lp-cta">
        <div className="container lp-ctaInner">
          <div className="lp-ctaText">
            <div className="lp-kicker lp-kicker--light">
              <i className="bi bi-rocket-takeoff" /> {t("cta_kicker")}
            </div>
            <h2>{t("cta_title")}</h2>
            <p>{t("cta_text")}</p>
          </div>

          <div className="lp-ctaActions">
            <button
              className="lp-btn lp-btnPrimary lp-btnPrimary--cta"
              onClick={() => {
                window.location.assign(
                  "https://weavevo.com/auth/realms/master/protocol/openid-connect/auth?client_id=weavevo-frontend&redirect_uri=https%3A%2F%2Fweavevo.com%2Fauth%2Fcallback&response_type=code&scope=openid&prompt=create&kc_idp_hint="
                );
              }}
            >
              <i className="bi bi-person-plus" />
              <span>{t("cta_btn_create")}</span>
            </button>

            <Link to="/decouvrir" className="lp-btn lp-btnGhost">
              <i className="bi bi-grid" />
              <span>{t("cta_btn_explore")}</span>
            </Link>
          </div>
        </div>
      </section>

      <FooterRunwayStyle />
    </div>
  );
}

export default LandingPage;
